import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Repeat } from "lucide-react";
import { toast } from "sonner";

const PLAN_LABEL = {
  plan_starter: "Starter",
  plan_growth: "Growth",
  plan_scale: "Scale",
};

export default function PlanSwitcher() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [picked, setPicked] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPlans(data));
    api.get("/me/subscription").then(({ data }) => {
      setSub(data);
      if (data.subscription_interval) setInterval(data.subscription_interval);
    });
  }, []);

  if (!sub) return null;

  const hasSub = Boolean(sub.subscription_status) && sub.plan !== "admin" && sub.plan !== "free";
  const isAdmin = sub.plan === "admin";
  const currentPlan = sub.plan;

  const change = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await api.post("/me/subscription/change", { plan_key: picked, interval });
      toast.success(`Switched to ${PLAN_LABEL[picked]} (${interval})`);
      await refresh();
      const { data } = await api.get("/me/subscription");
      setSub(data);
      setPicked(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Switch failed");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel at the end of your billing period?")) return;
    setCancelBusy(true);
    try {
      await api.post("/me/subscription/cancel");
      toast.success("Cancellation scheduled for period end");
      await refresh();
      const { data } = await api.get("/me/subscription");
      setSub(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <section className="mt-12" data-testid="plan-switcher">
      <div className="overline text-primary flex items-center gap-2">
        <CreditCard className="w-3.5 h-3.5" /> Subscription
      </div>
      <h2 className="mt-2 text-xl font-medium tracking-tight">
        {isAdmin ? "Admin — unlimited access" : hasSub ? "Change plan" : "You're on the free plan"}
      </h2>

      <div className="mt-4 rounded-xl border border-border bg-[#121214] p-6">
        {hasSub ? (
          <div className="text-sm text-muted-foreground">
            Current: <span className="text-foreground font-medium capitalize">{PLAN_LABEL[currentPlan] || currentPlan}</span>
            <span className="opacity-50 mx-2">·</span>
            <span className="capitalize">{sub.subscription_interval || "month"}ly</span>
            {sub.current_period_end && (
              <>
                <span className="opacity-50 mx-2">·</span>
                Renews {new Date(sub.current_period_end).toLocaleDateString()}
              </>
            )}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Pick a paid plan on the Pricing page to unlock weekly lead delivery.</div>
        )}

        <div className="mt-5 grid md:grid-cols-3 gap-3 items-end">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground uppercase tracking-[0.14em]">Plan</label>
            <Select value={picked || ""} onValueChange={setPicked}>
              <SelectTrigger data-testid="plan-switch-plan"><SelectValue placeholder="Choose a plan" /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.plan_key} value={p.plan_key} data-testid={`plan-opt-${p.plan_key}`}>
                    {p.name} — {p.weekly_leads}/wk
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground uppercase tracking-[0.14em]">Billing</label>
            <Select value={interval} onValueChange={setInterval}>
              <SelectTrigger data-testid="plan-switch-interval"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month" data-testid="interval-opt-month">Monthly</SelectItem>
                <SelectItem value="year" data-testid="interval-opt-year">Annual — save 17%</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={change}
            disabled={!picked || busy || !hasSub}
            className="rounded-full bg-primary hover:bg-[#FF7043] text-white h-10"
            data-testid="plan-switch-confirm"
          >
            <Repeat className="w-4 h-4 mr-1.5" /> {busy ? "Switching…" : (hasSub ? "Switch plan" : "Subscribe on Pricing")}
          </Button>
        </div>

        {hasSub && (
          <div className="mt-5 pt-5 border-t border-border flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              Switching upgrades or downgrades immediately with prorated billing.
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={cancel}
              disabled={cancelBusy}
              data-testid="plan-cancel-btn"
            >
              {cancelBusy ? "Canceling…" : "Cancel subscription"}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
